// ============================================================
// CODIVUS — Boot loader overlay
// ============================================================
// A simulated-progress modal shown while the site boots: a "LOADING..."
// caption, a progress bar, and the percent below it.
//
// showLoader() starts an eased ramp toward ~92% (the bar slows as it climbs
// and never reaches 100 on its own); finishLoader() snaps to 100% and fades
// out, holding for a minimum visible time so it never just flashes.
// setLoaderProgress() is there for callers that have a real percentage.
//
// Pure DOM — no framework — mirroring the toast() helper.

/** Never flash — keep the loader up at least this long once shown. */
const MIN_VISIBLE_MS = 600;
/** The simulated ramp eases toward this and never past it. */
const RAMP_CEILING = 92;

let rampTimer: ReturnType<typeof setInterval> | null = null;
let shownAt = 0;
let percent = 0;

function paint(): void {
  const fill = document.getElementById('loader-bar-fill');
  const pct = document.getElementById('loader-percent');
  if (!fill || !pct) return;
  const p = Math.round(percent);
  fill.style.width = `${p}%`;
  pct.textContent = `${p}%`;
}

function stopRamp(): void {
  if (rampTimer !== null) {
    clearInterval(rampTimer);
    rampTimer = null;
  }
}

/** Show the loader and begin the eased simulated ramp. */
export function showLoader(text = 'LOADING...'): void {
  stopRamp();
  percent = 0;
  shownAt = Date.now();

  let backdrop = document.getElementById('loader-backdrop');
  if (!backdrop) {
    backdrop = document.createElement('div');
    backdrop.id = 'loader-backdrop';
    backdrop.className = 'loader-backdrop';
    backdrop.innerHTML = `
      <div class="loader-card" role="status" aria-live="polite">
        <div class="loader-caption" id="loader-caption"></div>
        <div class="loader-bar-track"><div class="loader-bar-fill" id="loader-bar-fill"></div></div>
        <div class="loader-percent" id="loader-percent">0%</div>
      </div>`;
    document.body.appendChild(backdrop);
  }
  backdrop.classList.remove('loader-hide');
  const caption = document.getElementById('loader-caption');
  if (caption) caption.textContent = text;
  paint();

  rampTimer = setInterval(() => {
    // Close ~8% of the gap to the ceiling each tick (plus a small floor so it
    // always inches forward) — fast at first, asymptotically slower.
    percent = Math.min(RAMP_CEILING, percent + (RAMP_CEILING - percent) * 0.08 + 0.4);
    paint();
  }, 120);
}

/** Report a real percentage (0-100); stops the simulated ramp. */
export function setLoaderProgress(value: number): void {
  stopRamp();
  percent = Math.max(0, Math.min(100, value));
  paint();
}

/** Snap to 100%, hold for the minimum visible time, then fade out + remove. */
export function finishLoader(): void {
  stopRamp();
  percent = 100;
  paint();
  const wait = Math.max(0, MIN_VISIBLE_MS - (Date.now() - shownAt));
  // +260ms lets the bar animate to 100% before the fade-out begins.
  setTimeout(() => {
    const backdrop = document.getElementById('loader-backdrop');
    if (!backdrop) return;
    backdrop.classList.add('loader-hide');
    setTimeout(() => backdrop.remove(), 260);
  }, wait + 260);
}
